import React, { useState, useEffect } from 'react';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogFooter,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Checkbox } from '../../../ui/checkbox';
import { Spinner } from '../../../ui/spinner';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '../../../ui/select';
import { useTheme } from '../../../../contexts/theme/ThemeContext';
import {
 IconSpeakerphone,
 IconBuildingStore,
 IconCurrencyDollar,
 IconCalendarDue,
 IconUsers,
 IconLock,
 IconCheck,
 IconRocket,
 IconBrandDiscord,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';
import campaignService from '../../../../services/campaignService';
import brandService from '../../../../services/brandService';
import {
 Sheet,
 SheetContent,
 SheetHeader,
 SheetTitle,
 SheetFooter,
} from '../../../ui/sheet';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { cn } from '../../../../lib/utils';

const EditCampaignModal = ({ open, onClose, onSuccess, campaign }) => {
 const isMobile = useMediaQuery('(max-width: 639px)');
 const { isDarkMode: isDark } = useTheme();

 const [loading, setLoading] = useState(false);
 const [brands, setBrands] = useState([]);
 const [formData, setFormData] = useState({
 name: '',
 brandId: '',
 campaignType: 'REGULAR',
 creatorType: 'web3',
 pricingType: 'DEFAULT',
 hasBrief: true,
 requiresDrafts: false,
 isPrivate: false,
 twitterSource: '',
 briefingDeadline: '',
 deliveryDeadline: '',
 deadline: '',
 requiredCreators: '',
 minFollowers: '',
 priority: 'MEDIUM',
 });

 // Fetch brands and populate form when modal opens
 useEffect(() => {
 if (open) {
 fetchBrands();
 if (campaign) {
 populateFormData(campaign);
 }
 }
 }, [open, campaign]);

 const populateFormData = (campaignData) => {
 const formatDateForInput = (dateString) => {
 if (!dateString) return '';
 try {
 const date = new Date(dateString);
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 return `${year}-${month}-${day}`;
 } catch (e) {
 console.error('Error formatting date:', dateString, e);
 return '';
 }
 };

 setFormData({
 name: campaignData.name || '',
 brandId: campaignData.brandId?._id || campaignData.brandId || '',
 campaignType: campaignData.campaignType || 'REGULAR',
 creatorType: Array.isArray(campaignData.creatorType) ? (campaignData.creatorType[0] || 'web3') : (campaignData.creatorType || 'web3'),
 pricingType: campaignData.pricingType || 'DEFAULT',
 hasBrief: campaignData.hasBrief !== undefined ? campaignData.hasBrief : true,
 requiresDrafts: campaignData.requiresDrafts || false,
 isPrivate: campaignData.isPrivate || false,
 twitterSource: campaignData.twitterSource || '',
 briefingDeadline: formatDateForInput(campaignData.briefingDeadline),
 deliveryDeadline: formatDateForInput(campaignData.deliveryDeadline),
 deadline: formatDateForInput(campaignData.endDate),
 requiredCreators: campaignData.requiredCreators?.toString() || '',
 minFollowers: campaignData.minFollowers?.toString() || '',
 priority: campaignData.priority || 'MEDIUM',
 });

 };

 const fetchBrands = async () => {
 try {
 const response = await brandService.getAllBrands();
 setBrands(Array.isArray(response) ? response : response.data || []);
 } catch (error) {
 console.error('Error fetching brands:', error);
 toast.error('Failed to load brands');
 }
 };

 const handleChange = (field, value) => {
 setFormData((prev) => ({ ...prev, [field]: value }));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!formData.name?.trim() || !formData.brandId?.trim() || !formData.deadline?.trim()) {
 toast.error('Please fill in all required fields');
 return;
 }

 try {
 setLoading(true);

 const hasBrief = formData.hasBrief === true;
 const isCommentBasedNoBrief = formData.campaignType === 'COMMENT_BASED' && !hasBrief;
 const isAmplification = formData.campaignType === 'AMPLIFICATION';

 const campaignData = {
 name: formData.name.trim(),
 brandId: formData.brandId,
 campaignType: formData.campaignType,
 creatorType: [formData.creatorType || 'web3'],
 pricingType: formData.pricingType,
 hasBrief: formData.campaignType === 'COMMENT_BASED' ? formData.hasBrief : true,
 requiresDrafts: formData.requiresDrafts,
 isPrivate: formData.isPrivate,
 twitterSource: formData.twitterSource?.trim(),
 startDate: new Date().toISOString(),
 endDate: new Date(formData.deadline).toISOString(),
 briefingDeadline:
 !isCommentBasedNoBrief && !formData.isPrivate && formData.briefingDeadline && !isAmplification
 ? new Date(formData.briefingDeadline).toISOString()
 : null,
 deliveryDeadline:
 !isCommentBasedNoBrief && !formData.isPrivate && formData.deliveryDeadline && !isAmplification
 ? new Date(formData.deliveryDeadline).toISOString()
 : null,
 minFollowers: parseInt(formData.minFollowers) || 0,
 requiredCreators:
 isCommentBasedNoBrief || formData.isPrivate || isAmplification
 ? 0
 : parseInt(formData.requiredCreators) || 1,
 priority: formData.priority || 'MEDIUM',
 };

 const response = await campaignService.updateCampaign(campaign._id, campaignData);

 if (!response) {
 throw new Error('No response received from server');
 }

 toast.success('Campaign updated successfully');

 setFormData({
 name: '',
 brandId: '',
 campaignType: 'REGULAR',
 creatorType: 'web3',
 pricingType: 'DEFAULT',
 hasBrief: true,
 requiresDrafts: false,
 isPrivate: false,
 twitterSource: '',
 briefingDeadline: '',
 deliveryDeadline: '',
 deadline: '',
 requiredCreators: '',
 minFollowers: '',
 priority: 'MEDIUM',
 });

 if (onSuccess) {
 onSuccess(response);
 }

 onClose();
 } catch (error) {
 console.error('Error updating campaign:', error);
 toast.error(error.message || 'Failed to update campaign');
 } finally {
 setLoading(false);
 }
 };

 const handleCampaignTypeChange = (type) => {
 setFormData((prev) => ({
 ...prev,
 campaignType: type,
 pricingType: type === 'AMPLIFICATION' ? 'QRT' : prev.pricingType,
 }));
 };

 // Selectable card helper
 const SelectableCard = ({ selected, onClick, children, className }) => (
 <div
 onClick={onClick}
 className={cn(
 'p-4 cursor-pointer border',
 selected
 ? 'border-accent bg-accent/5 hover:bg-accent/10'
 : 'border-glass-border hover:border-glass-border-hover',
 !selected && (isDark ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-black/[0.01] hover:bg-black/[0.02]'),
 className
 )}
 >
 {children}
 </div>
 );

 const formContent = (
 <form onSubmit={handleSubmit}>
 <div className="flex flex-col gap-4 py-4">
 {/* Campaign Name & Brand Selection - Same Row */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
 {/* Campaign Name */}
 <div className="sm:col-span-2">
 <Label htmlFor="edit-campaign-name" className="mb-2 block">Campaign Name *</Label>
 <div className="relative">
 <IconSpeakerphone
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-campaign-name"
 required
 value={formData.name}
 onChange={(e) => handleChange('name', e.target.value)}
 className="pl-10"
 placeholder="Enter campaign name"
 />
 </div>
 </div>

 {/* Brand Selection */}
 <div>
 <Label htmlFor="edit-brand-select" className="mb-2 block">Brand *</Label>
 <Select
 value={formData.brandId}
 onValueChange={(value) => handleChange('brandId', value)}
 >
 <SelectTrigger>
 <div className="flex items-center gap-2">
 <IconBuildingStore size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue placeholder="Select a brand" />
 </div>
 </SelectTrigger>
 <SelectContent>
 {brands.map((brand) => (
 <SelectItem key={brand._id} value={brand._id}>
 {brand.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {/* Campaign Type Selection */}
 <div>
 <Label className="mb-3 block text-text-secondary">Campaign Type</Label>
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
 {[
 { type: 'REGULAR', icon: IconSpeakerphone, label: 'Regular', desc: 'Standard campaign with briefing' },
 { type: 'COMMENT_BASED', icon: IconBrandDiscord, label: 'Comment-Based', desc: 'Twitter comment tracking' },
 { type: 'AMPLIFICATION', icon: IconRocket, label: 'Amplification', desc: '4-week automated QRT/comment series' },
 ].map(({ type, icon: Icon, label, desc }) => (
 <SelectableCard
 key={type}
 selected={formData.campaignType === type}
 onClick={() => handleCampaignTypeChange(type)}
 >
 <div className="flex flex-col gap-1">
 <div className="flex items-center gap-1">
 <Icon size={16} className={formData.campaignType === type ? 'text-accent' : 'text-text-tertiary'} />
 <span className="text-sm font-medium text-text-primary">{label}</span>
 {formData.campaignType === type && (
 <IconCheck size={14} className="text-accent ml-auto" />
 )}
 </div>
 <span className="text-xs text-text-tertiary leading-snug">{desc}</span>
 </div>
 </SelectableCard>
 ))}
 </div>
 </div>

 {/* Creator Audience */}
 <div>
 <Label className="mb-2 block">Creator Audience</Label>
 <Select
 value={formData.creatorType}
 onValueChange={(value) => handleChange('creatorType', value)}
 >
 <SelectTrigger>
 <div className="flex items-center gap-2">
 <IconUsers size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue />
 </div>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="web3">Web3 Creators</SelectItem>
 <SelectItem value="ai">AI Creators</SelectItem>
 </SelectContent>
 </Select>
 </div>

 {/* Pricing Type - Hide for AMPLIFICATION */}
 {formData.campaignType !== 'AMPLIFICATION' && (
 <div>
 <Label className="mb-2 block">Pricing Type</Label>
 <Select
 value={formData.pricingType}
 onValueChange={(value) => handleChange('pricingType', value)}
 >
 <SelectTrigger>
 <div className="flex items-center gap-2">
 <IconCurrencyDollar size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue />
 </div>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="DEFAULT">Default Pricing</SelectItem>
 <SelectItem value="QRT">QRT Pricing</SelectItem>
 <SelectItem value="SHORT_FORM">Short Form Pricing</SelectItem>
 </SelectContent>
 </Select>
 </div>
 )}

 {/* Privacy Setting - Hide for AMPLIFICATION */}
 {formData.campaignType !== 'AMPLIFICATION' && (
 <div>
 <Label className="mb-3 block text-text-secondary">Campaign Privacy</Label>
 <div className="grid grid-cols-2 gap-3">
 {[
 { value: false, icon: IconUsers, label: 'Public', desc: 'Standard workflow with briefing message' },
 { value: true, icon: IconLock, label: 'Private', desc: 'No briefing message, direct to onboarding' },
 ].map(({ value, icon: Icon, label, desc }) => (
 <SelectableCard
 key={label}
 selected={formData.isPrivate === value}
 onClick={() => handleChange('isPrivate', value)}
 >
 <div className="flex items-center gap-1 mb-1">
 <Icon size={16} className={formData.isPrivate === value ? 'text-accent' : 'text-text-tertiary'} />
 <span className="text-sm font-medium text-text-primary">{label}</span>
 {formData.isPrivate === value && (
 <IconCheck size={14} className="text-accent ml-auto" />
 )}
 </div>
 <span className="text-xs text-text-tertiary leading-snug">{desc}</span>
 </SelectableCard>
 ))}
 </div>
 </div>
 )}

 {/* Has Brief - Only for COMMENT_BASED */}
 {formData.campaignType === 'COMMENT_BASED' && !formData.isPrivate && (
 <div>
 <Label className="mb-3 block text-text-secondary">Include Briefing Stage?</Label>
 <div className="grid grid-cols-2 gap-3">
 {[
 { value: true, label: 'Yes' },
 { value: false, label: 'No' },
 ].map(({ value, label }) => (
 <SelectableCard
 key={label}
 selected={formData.hasBrief === value}
 onClick={() => handleChange('hasBrief', value)}
 >
 <div className="flex items-center justify-center">
 <span className="text-sm font-medium text-text-primary">{label}</span>
 {formData.hasBrief === value && (
 <IconCheck size={14} className="text-accent ml-2" />
 )}
 </div>
 </SelectableCard>
 ))}
 </div>
 </div>
 )}

 {/* Require Drafts - Only for REGULAR with hasBrief */}
 {formData.campaignType === 'REGULAR' && formData.hasBrief && (
 <div className="flex items-start gap-3">
 <Checkbox
 id="edit-require-drafts"
 checked={formData.requiresDrafts}
 onCheckedChange={(checked) => handleChange('requiresDrafts', checked)}
 className="mt-0.5"
 />
 <div>
 <Label htmlFor="edit-require-drafts" className="cursor-pointer">Require Draft Reviews?</Label>
 <p className="text-xs text-text-secondary mt-0.5">
 Creators must submit drafts for review before posting
 </p>
 </div>
 </div>
 )}

 {/* Deadlines - Hide for AMPLIFICATION */}
 {formData.campaignType !== 'AMPLIFICATION' && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="edit-briefing-deadline" className="mb-2 block">Briefing Deadline</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-briefing-deadline"
 type="date"
 value={formData.briefingDeadline}
 onChange={(e) => handleChange('briefingDeadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 <div>
 <Label htmlFor="edit-delivery-deadline" className="mb-2 block">Delivery Deadline</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-delivery-deadline"
 type="date"
 value={formData.deliveryDeadline}
 onChange={(e) => handleChange('deliveryDeadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 </div>
 )}

 {/* Final Deadline */}
 <div>
 <Label htmlFor="edit-final-deadline" className="mb-2 block">Final Deadline *</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-final-deadline"
 type="date"
 required
 value={formData.deadline}
 onChange={(e) => handleChange('deadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>

 {/* Required Creators & Min Followers - Hide for AMPLIFICATION */}
 {formData.campaignType !== 'AMPLIFICATION' && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="edit-required-creators" className="mb-2 block">Required Creators</Label>
 <div className="relative">
 <IconUsers
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-required-creators"
 type="number"
 value={formData.requiredCreators}
 onChange={(e) => handleChange('requiredCreators', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 <div>
 <Label htmlFor="edit-min-followers" className="mb-2 block">Minimum Followers</Label>
 <div className="relative">
 <IconUsers
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="edit-min-followers"
 type="number"
 value={formData.minFollowers}
 onChange={(e) => handleChange('minFollowers', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 </div>
 )}

 {/* Priority */}
 <div>
 <Label className="mb-2 block">Priority</Label>
 <Select
 value={formData.priority}
 onValueChange={(value) => handleChange('priority', value)}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="LOW">Low</SelectItem>
 <SelectItem value="MEDIUM">Medium</SelectItem>
 <SelectItem value="HIGH">High</SelectItem>
 <SelectItem value="URGENT">Urgent</SelectItem>
 <SelectItem value="CRITICAL">Critical</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div className="flex justify-end gap-2 border-t border-glass-border pt-4">
 <Button
 type="button"
 variant="ghost"
 onClick={onClose}
 disabled={loading}
 >
 Cancel
 </Button>
 <Button
 type="submit"
 disabled={loading}
 >
 {loading && <Spinner size="sm" className="mr-2" />}
 {loading ? 'Saving...' : 'Save Changes'}
 </Button>
 </div>
 </form>
 );

 if (isMobile) {
 return (
 <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
 <SheetContent side="bottom" className="h-[95vh] overflow-y-auto p-4">
 <SheetHeader className="mb-2">
 <SheetTitle>Edit Campaign</SheetTitle>
 </SheetHeader>
 {formContent}
 </SheetContent>
 </Sheet>
 );
 }

 return (
 <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
 <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Edit Campaign</DialogTitle>
 </DialogHeader>
 {formContent}
 </DialogContent>
 </Dialog>
 );
};

export default EditCampaignModal;
